/* eslint-disable max-lines */
import React, {
  Component,
  useState,
  useEffect,
  useRef,
  useCallback
} from "react";
import { Modal, Icon } from "antd";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

import config from "appConfig";
import {
  parseFormControlValue,
  buildBaseUrl,
  notification,
  asset,
  convertBlobToBase64
} from "../../../services";
import styles from "./photoUploader.scss";
import { compressImageRequest } from "./compressImageRequest";
import MultiplePhotos from "./MultiplePhotos";
import { photoEditAction } from "../../../models";

const UploadSuccessStatus = "done";

const PhotoUploader = props => {
  const {
    title = "",
    isRequired = false,
    maxPhotoNumber = 8,
    uploadClassName = "",
    validImageTypes = ["image/jpeg", "image/png"],
    field = "",
    onChange,
    maxImageSize = 8,
    isCompressed = true,
    isDisabled = false,
    previewCardClassName = "",
    uiComponent: UIComponent = MultiplePhotos,
    value,
    setIsValidating
  } = props;

  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [uploadAcceptTypes, setUploadAcceptTypes] = useState(
    validImageTypes.join()
  );

  const uploadEndpoint = buildBaseUrl(`${config.imageUploadEndpoint}`);

  const editPhotoData = useRef();
  const previewImage = useRef("");
  const maxFileSize = maxImageSize * 1024 * 1024;
  const shouldNotifyError = useRef(true);
  const cropper = useRef();

  const isMultiValues = maxPhotoNumber > 1;

  useEffect(() => {
    const { fileList } = getValueState(props);
    setFileList(fileList);
  }, []);

  useEffect(() => {
    setUploadAcceptTypes(validImageTypes.join());
  }, [validImageTypes]);

  const getValueState = props => {
    const { value, field } = props;

    if (value) {
      let images = [value];
      if (field) {
        images = value[field];
      } else if (isMultiValues) {
        images = value;
      }

      const newFileList = fileList.slice();
      images.forEach(imageName => {
        if (
          !newFileList.find(
            file =>
              (file.response && file.response[0] === imageName) ||
              file.uid === imageName
          )
        ) {
          newFileList.push({
            uid: imageName,
            status: UploadSuccessStatus,
            url: asset.getImageLink(imageName)
          });
        }
      });

      return { fileList: newFileList };
    }
    return { fileList: [] };
  };

  const handleCancel = () => setPreviewVisible(false);

  const handlePreview = async url => {
    previewImage.current = url;

    setPreviewVisible(true);

    if (cropper.current) {
      // @ts-ignore
      editPhotoData.current = await fetch(url)
        .then(response => {
          if (response.ok) {
            return response.blob();
          }

          return null;
        })
        .then(convertBlobToBase64);

      cropper.current = editPhotoData.current;
    }
  };

  const handleValueChange = value => {
    const { onChange, field, value: valueProp } = props;
    if (field) {
      let val = parseFormControlValue([field], [value]);
      if (!val) {
        if (valueProp && valueProp[field].length > 0) {
          val = {};
          val[field] = [];
        }
      }
      onChange(val);
    } else if (isMultiValues) {
      onChange(value);
    } else {
      onChange(value && value.length === 1 ? value[0] : null);
    }
  };

  function getFileId(file) {
    return (file.response && file.response[0]) || file.uid;
  }

  const handleChange = ({ fileList }) => {

    const value = [];
    const filteredFiles = fileList.filter(file => file.status);

    let totalCount = filteredFiles.length,
      doneCount = 0;

    totalCount > 0 && setIsValidating(true);

    filteredFiles.forEach(file => {
      if (file.status === UploadSuccessStatus) {
        value.push(getFileId(file));
        doneCount += 1;
      }
    });

    setFileList(filteredFiles);

    doneCount >= totalCount && setIsValidating(false);
    doneCount >= totalCount && handleValueChange(value)
  };

  const beforeUpload = (file, selectedFiles) => {
    const { validImageTypes, maxPhotoNumber, maxImageSize } = props;

    const fileIndex = selectedFiles.findIndex(f => f.uid === file.uid);
    if (fileIndex === 0) {
      shouldNotifyError.current = true;
    }

    if (selectedFiles.length + fileList.length > maxPhotoNumber) {
      notifyError(
        "Ne možete objaviti više od {0} slika!".replace("{0}", String(maxPhotoNumber))
      );

      return false;
    }

    if (file.size > maxFileSize) {
      notifyError(
        "Maksimalna veličina datoteke je {0} MB!".replace("{0}", String(maxImageSize))
      );

      return false;
    }

    const isImage = validImageTypes.includes(file.type);
    if (!isImage) {
      notification.showError("Možete učitati samo slikovnu datoteku u formatima JPG, JPEG i PNG!");
    }

    return isImage;
  };

  const notifyError = message => {
    if (shouldNotifyError.current) {
      notification.showError(message);
      shouldNotifyError.current = false;
    }
  };

  const handleRemove = index => {
    const newList = [...fileList];
    newList.splice(index, 1);
    handleChange({ fileList: newList });
  };

  const handleMove = (fromIndex, toIndex) => {
    const newList = [...fileList];
    const from = newList.splice(fromIndex, 1);
    newList.splice(toIndex, 0, from[0]);
    handleChange({ fileList: newList });
  };

  const applyEditPhoto = () => {
    const canvas =
      // @ts-ignore
      cropper && cropper.current && cropper.current.getCroppedCanvas();

    const editingFile = fileList.find(
      file =>
        // @ts-ignore
        previewImage.current && previewImage.current.includes(getFileId(file))
    );

    // @ts-ignore
    editPhotoData.current = canvas && canvas.toDataURL(editingFile.type);

    // @ts-ignore
    canvas.toBlob(blob => {
      const editedFile = new File([blob], editingFile.name, {
        type: blob.type
      });

      compressImageRequest(false, {
        action: uploadEndpoint,
        data: {},
        file: editedFile,
        filename: editingFile.name,
        onError: () => null,
        onProgress: () => null,
        onSuccess: response => {
          const newFiles = fileList.map(file => {
            if (editingFile === file) {
              if (!file.response) {
                // when edit ad the file doesn't contain uid so have to add it manually
                return {
                  ...file,
                  uid: response[0],
                  url: asset.getImageLink(response[0])
                };
              }

              return {
                ...file,
                response
              };
            }

            return file;
          });

          setFileList(newFiles);
          setPreviewVisible(false);

          handleValueChange(newFiles.map(file => getFileId(file)));
        }
      });
    }, editingFile.type);
  };

  const handleRenderedEditor = async ref => {
    if (ref) {
      // @ts-ignore
      editPhotoData.current = await fetch(previewImage.current)
        .then(response => {
          if (response.ok) {
            return response.blob();
          }

          return null;
        })
        .then(convertBlobToBase64);

      ref.onload = () => {
        // @ts-ignore
        cropper.current = new Cropper(ref, {
          background: false,
          autoCrop: false,
          checkOrientation: false
        });
      };
      ref.src = editPhotoData.current;
    }
  };

  const handleEditAction = action => {
    switch (action) {
      case photoEditAction.rotateLeft:
        // @ts-ignore
        cropper.current.rotate(-90);
        break;
      case photoEditAction.rotateRight:
        // @ts-ignore
        cropper.current.rotate(90);
        break;
      case photoEditAction.flipHorizontal:
        // @ts-ignore
        cropper.current.scaleX(-cropper.current.getData().scaleX || -1);
        break;
      case photoEditAction.flipVertical:
        // @ts-ignore
        cropper.current.scaleY(-cropper.current.getData().scaleY || -1);
        break;
      case photoEditAction.reset:
        // @ts-ignore
        cropper.current.rotateTo(0);
        // @ts-ignore
        cropper.current.scaleX(1);
        // @ts-ignore
        cropper.current.scaleY(1);
        break;
      default:
        // @ts-ignore
        cropper.current = editPhotoData.current;
        break;
    }
  };

  function renderPreviewModal() {
    return (
      <Modal
        width="90%"
        className={styles.modal}
        visible={previewVisible}
        onCancel={handleCancel}
        okText={"Primjeni"}
        onOk={applyEditPhoto}
        destroyOnClose
      >
        <div className={styles.content}>
          <img
            className={styles.photoEditor}
            alt="Uploaded"
            ref={handleRenderedEditor}
          />
          <div className={styles.controls}>
            <Icon
              className={styles.rotateLeft}
              type="reload"
              title="Zakreni lijevo"
              onClick={e => {
                handleEditAction(photoEditAction.rotateLeft);
              }}
            />
            <Icon
              className={styles.rotateRight}
              type="reload"
              title="Zakreni desno"
              onClick={e => {
                handleEditAction(photoEditAction.rotateRight);
              }}
            />
            <Icon
              className={styles.flipHorizontal}
              type="swap"
              title="Okreni vodoravno"
              onClick={e => {
                handleEditAction(photoEditAction.flipHorizontal);
              }}
            />
            <Icon
              className={styles.flipVertical}
              type="swap"
              title="Okreni okomito"
              onClick={e => {
                handleEditAction(photoEditAction.flipVertical);
              }}
            />
            <Icon
              className={styles.reset}
              type="sync"
              title="Resetiraj"
              onClick={e => {
                handleEditAction(photoEditAction.reset);
              }}
            />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <React.Fragment>
      <span className={`control-title ${isRequired ? "required" : ""}`}>
        {title}
      </span>
      <UIComponent
        disabled={isDisabled}
        draggable={isMultiValues}
        onMove={handleMove}
        files={fileList}
        onPreview={handlePreview}
        onRemove={handleRemove}
        action={uploadEndpoint}
        uploadClassName={uploadClassName}
        previewCardClassName={previewCardClassName}
        listType="picture-card"
        onChange={handleChange}
        beforeUpload={beforeUpload}
        accept={uploadAcceptTypes}
        multiple={isMultiValues}
        customRequest={compressImageRequest.bind(this, isCompressed)}
        showUploadList={false}
        maxPhotoNumber={maxPhotoNumber}
      />
      {renderPreviewModal()}
    </React.Fragment>
  );
};

PhotoUploader.defaultProps = {
  title: "",
  isRequired: false,
  maxPhotoNumber: 8,
  uploadClassName: "",
  validImageTypes: ["image/jpeg", "image/png"],
  field: "",
  onChange: () => null,
  maxImageSize: 8,
  isCompressed: true,
  isDisabled: false,
  previewCardClassName: "",
  uiComponent: MultiplePhotos,
  setIsValidating: () => null
};

export default PhotoUploader;



// WEBPACK FOOTER //
// ./src/components/FormControls/PhotoUploader/PhotoUploader.tsx